import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Letterhead, type LetterheadCompany } from '@/components/company/Letterhead';
import { Button } from '@/components/ui/button';
import { ErrorState, LoadingState } from '@/components/ui/states';
import {
  PublicAppointmentEmailsGoneError,
  usePublicAppointmentEmails,
  useSetPublicAppointmentEmails,
  type PublicAppointmentEmails as Preference,
} from '@/routes/publicAppointmentEmails/usePublicAppointmentEmails';

/**
 * The recipient's own appointment-email page (M2-21, Kyle's 8), opened from
 * the link at the foot of every confirmation, reminder and "on my way" — with
 * no sign-in. M2-16's `/i/:token`, rule for rule: routed OUTSIDE
 * `RequireAuth` and `AppLayout`, read through `publicClient` (no bearer, no
 * 401 redirect to `/signin`), every dead link one "no longer valid" page that
 * names no shop, the token redacted from telemetry (`redactLinkToken`).
 *
 * <b>Nothing changes on load.</b> Mail scanners and link previewers open every
 * link in an email; an unsubscribe that happened on a GET would unsubscribe
 * people who never clicked. Turning emails off is a button and a POST, and
 * the page then offers an undo — another POST.
 *
 * It shows the shop and the recipient's address MASKED, exactly as the API
 * gives it, and nothing about the job or the appointment: a forwarded email's
 * link shows a stranger no more than that.
 */
export function PublicAppointmentEmails() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const preference = usePublicAppointmentEmails(token);

  if (preference.isPending) {
    return (
      <Page>
        <LoadingState label={t('publicAppointmentEmails.loading')} className="justify-center" />
      </Page>
    );
  }

  if (preference.error instanceof PublicAppointmentEmailsGoneError) {
    return <Gone />;
  }

  if (preference.isError) {
    return (
      <Page>
        <ErrorState
          title={t('publicAppointmentEmails.loadErrorTitle')}
          description={t('publicAppointmentEmails.loadErrorDescription')}
          onRetry={() => void preference.refetch()}
        />
      </Page>
    );
  }

  return <Loaded token={token} preference={preference.data} />;
}

function Gone() {
  const { t } = useTranslation();
  return (
    <Page>
      <section aria-labelledby="public-appointments-gone" className="space-y-3 pt-6 text-center">
        <h1 id="public-appointments-gone" className="text-xl font-semibold tracking-tight">
          {t('publicAppointmentEmails.goneTitle')}
        </h1>
        <p className="mx-auto max-w-prose text-sm text-[var(--color-muted-foreground)]" data-testid="explained">
          {t('publicAppointmentEmails.goneDescription')}
        </p>
      </section>
    </Page>
  );
}

/**
 * What the last press here did, so the page can say so and offer the undo.
 * `null` until a button is pressed — a recipient who arrives already opted
 * out is shown the plain "turned off" state with "Turn back on", not an undo
 * of something they did not just do.
 */
type Done = 'turnedOff' | 'turnedOn' | null;

function Loaded({ token, preference }: { token: string | undefined; preference: Preference }) {
  const { t } = useTranslation();
  const save = useSetPublicAppointmentEmails(token);
  const [done, setDone] = useState<Done>(null);
  const [gone, setGone] = useState(false);
  const shop = preference.shopName;
  const letterhead: LetterheadCompany = {
    name: shop,
    address: preference.company?.address ?? null,
    phone: preference.company?.phone ?? null,
    licenseNumber: preference.company?.licenseNumber ?? null,
    logoPath: preference.company?.logoPath ?? null,
  };

  useDocumentTitle(t('publicAppointmentEmails.documentTitle', { company: shop }));

  if (gone) return <Gone />;

  function set(enabled: boolean) {
    save.mutate(enabled, {
      onSuccess: () => setDone(enabled ? 'turnedOn' : 'turnedOff'),
      onError: (error) => {
        if (error instanceof PublicAppointmentEmailsGoneError) setGone(true);
      },
    });
  }

  const address = preference.maskedAddress;
  const pending = save.isPending;

  return (
    <Page company={letterhead}>
      <article aria-labelledby="public-appointments-title" className="space-y-5">
        <div className="space-y-1">
          <h1 id="public-appointments-title" className="text-2xl font-semibold tracking-tight">
            {t('publicAppointmentEmails.title')}
          </h1>
          {address ? (
            <p className="text-sm break-words text-[var(--color-muted-foreground)]" data-testid="public-address">
              {t('publicAppointmentEmails.forAddress', { address })}
            </p>
          ) : null}
        </div>

        <p
          className={
            preference.enabled
              ? 'rounded-md border-l-4 border-[var(--color-primary)] bg-[var(--color-muted)] px-4 py-3'
              : 'rounded-md border-l-4 border-[var(--color-border)] bg-[var(--color-muted)] px-4 py-3'
          }
          data-testid="public-state"
        >
          {preference.enabled
            ? t('publicAppointmentEmails.onState', { company: shop })
            : t('publicAppointmentEmails.offState', { company: shop })}
        </p>

        {/* What the last press did, announced. Its own region, present from
            the start, so the change is read out rather than only drawn. */}
        <p role="status" aria-live="polite" className="text-sm font-medium" data-testid="public-done">
          {done === 'turnedOff'
            ? t('publicAppointmentEmails.turnedOff', { company: shop })
            : done === 'turnedOn'
              ? t('publicAppointmentEmails.turnedOn', { company: shop })
              : ''}
        </p>

        <div className="flex flex-col gap-3 sm:flex-row">
          {preference.enabled ? (
            <Button type="button" className="w-full sm:w-auto" disabled={pending} onClick={() => set(false)}>
              {pending ? t('publicAppointmentEmails.saving') : t('publicAppointmentEmails.turnOff')}
            </Button>
          ) : done === 'turnedOff' ? (
            <Button type="button" className="w-full sm:w-auto" disabled={pending} onClick={() => set(true)}>
              {pending ? t('publicAppointmentEmails.saving') : t('publicAppointmentEmails.undo')}
            </Button>
          ) : (
            <Button type="button" className="w-full sm:w-auto" disabled={pending} onClick={() => set(true)}>
              {pending ? t('publicAppointmentEmails.saving') : t('publicAppointmentEmails.turnOn')}
            </Button>
          )}
        </div>

        {save.isError && !(save.error instanceof PublicAppointmentEmailsGoneError) ? (
          <p role="alert" className="text-sm text-[var(--color-destructive)]">
            {t('publicAppointmentEmails.saveFailed')}
          </p>
        ) : null}

        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('publicAppointmentEmails.otherEmails', { company: shop })}
        </p>
      </article>
    </Page>
  );
}

/** The page's frame: the shop's letterhead as the header, then one readable column. */
function Page({ company, children }: { company?: LetterheadCompany; children: ReactNode }) {
  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {company ? (
        <header className="border-b border-[var(--color-border)]">
          <div className="mx-auto max-w-2xl px-4 py-4">
            <Letterhead company={company} />
          </div>
        </header>
      ) : null}
      <main className="mx-auto max-w-2xl space-y-6 px-4 py-6">{children}</main>
    </div>
  );
}

/** The tab's title while the page is open. */
function useDocumentTitle(title: string) {
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}
